import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/ModulePage";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { RagBadge } from "../components/Badges";
import { PhotoUpload, Thumb } from "../components/PhotoUpload";
import { SignaturePad } from "../components/SignaturePad";
import { EscalationTrail } from "../components/EscalationTrail";

const NewIncident = ({ open, onOpenChange, onDone }) => {
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF({ type: "accident", date: new Date().toISOString().slice(0, 10), severity: "medium", photos: [] });
  }, [open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async () => {
    try {
      await api.post("/incidents", f);
      toast.success("Incident reported - managers notified");
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-700 bg-ink-2 sm:max-w-xl" data-testid="new-incident-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Report accident / near miss</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <select className="field" value={f.type} onChange={set("type")} data-testid="incident-type"><option value="accident">Accident</option><option value="near_miss">Near miss</option><option value="dangerous_occurrence">Dangerous occurrence</option></select>
          <input type="date" className="field" value={f.date || ""} onChange={set("date")} data-testid="incident-date" />
          <input className="field sm:col-span-2" placeholder="Short title *" value={f.title || ""} onChange={set("title")} data-testid="incident-title" />
          <input className="field" placeholder="Location" value={f.location || ""} onChange={set("location")} data-testid="incident-location" />
          <select className="field" value={f.severity} onChange={set("severity")} data-testid="incident-severity"><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select>
          <textarea className="field h-24 py-2 sm:col-span-2" placeholder="What happened?" value={f.description || ""} onChange={set("description")} data-testid="incident-description" />
          <input className="field" placeholder="Persons involved" value={f.persons_involved || ""} onChange={set("persons_involved")} data-testid="incident-persons" />
          <input className="field" placeholder="Witnesses" value={f.witnesses || ""} onChange={set("witnesses")} data-testid="incident-witnesses" />
          <input className="field sm:col-span-2" placeholder="Injury details" value={f.injury || ""} onChange={set("injury")} data-testid="incident-injury" />
          <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" className="h-5 w-5 accent-rose-600" checked={!!f.riddor_reportable} onChange={set("riddor_reportable")} data-testid="incident-riddor" />Potentially RIDDOR reportable</label>
          <div className="sm:col-span-2"><PhotoUpload value={f.photos} onChange={(photos) => setF({ ...f, photos })} testid="incident-photos" /></div>
          <Button onClick={submit} disabled={!f.title} className="tap bg-rose-600 hover:bg-rose-700 sm:col-span-2" data-testid="incident-submit-btn">Submit report</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CAPA_FIELDS = [["corrective_action", "Corrective action - what was fixed?"], ["preventive_action", "Preventive action - how is recurrence prevented?"], ["verification_notes", "Verification - how was the fix verified?"]];

const IncidentDetail = ({ inc, onClose, onChange }) => {
  const { isManager, isApprover, user } = useAuth();
  const [capa, setCapa] = useState({});
  const [sig, setSig] = useState(null);
  useEffect(() => { setCapa(inc?.capa || {}); setSig(null); }, [inc]);
  if (!inc) return null;
  const closed = inc.status === "closed";
  const canClose = isApprover || user.role === "supervisor";
  const set = (k) => (e) => setCapa({ ...capa, [k]: e.target.value });
  const save = async () => {
    try {
      const r = await api.put(`/incidents/${inc.id}`, { capa });
      toast.success("CAPA saved");
      onChange(r.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const close = async () => {
    try {
      const r = await api.post(`/incidents/${inc.id}/close`, { signature: sig });
      toast.success("Incident closed");
      onChange(r.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const ro = closed || !isManager;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-slate-700 bg-ink-2 sm:max-w-2xl" data-testid="incident-detail-dialog">
        <DialogHeader>
          <p className="font-mono text-xs text-cyan-300">{inc.ref} - {inc.type.replace(/_/g, " ").toUpperCase()}{inc.riddor_reportable ? " - RIDDOR" : ""}</p>
          <DialogTitle className="font-display text-2xl uppercase">{inc.title}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-2 text-sm sm:grid-cols-2">
          <p><span className="eyebrow">Date </span>{fmtDate(inc.date)}</p><p><span className="eyebrow">Location </span>{inc.location || "-"}</p>
          <p><span className="eyebrow">Persons </span>{inc.persons_involved || "-"}</p><p><span className="eyebrow">Witnesses </span>{inc.witnesses || "-"}</p>
          <p className="sm:col-span-2 text-slate-300">{inc.description}</p>
        </div>
        {inc.photos?.length > 0 && <div className="flex flex-wrap gap-2">{inc.photos.map((p) => <Thumb key={p} src={p} />)}</div>}
        {!closed && <EscalationTrail doc={inc} testid="incident-escalation" />}
        <div className="space-y-3 rounded-md border border-slate-700 p-4" data-testid="capa-section">
          <p className="font-display text-lg font-bold uppercase text-rose-300">Mandatory CAPA</p>
          <select className="field" disabled={ro} value={capa.root_cause_category || ""} onChange={set("root_cause_category")} data-testid="capa-root-cause-category">
            <option value="">Root cause category...</option>
            {["Unsafe condition", "Unsafe act", "Training issue", "Equipment failure", "Procedure / system failure", "Other"].map((o) => <option key={o}>{o}</option>)}
          </select>
          <textarea className="field h-16 py-2" disabled={ro} placeholder="Root cause description" value={capa.root_cause || ""} onChange={set("root_cause")} data-testid="capa-root-cause" />
          {CAPA_FIELDS.map(([k, l]) => <textarea key={k} className="field h-16 py-2" disabled={ro} placeholder={l} value={capa[k] || ""} onChange={set(k)} data-testid={`capa-${k}`} />)}
          <div className="grid gap-3 sm:grid-cols-2">
            <input className="field" disabled={ro} placeholder="Action owner" value={capa.action_owner || ""} onChange={set("action_owner")} data-testid="capa-owner" />
            <input type="date" className="field" disabled={ro} value={capa.due_date || ""} onChange={set("due_date")} data-testid="capa-due-date" />
          </div>
          <p className="eyebrow">Verification evidence (required)</p>
          {ro ? <div className="flex flex-wrap gap-2">{(capa.evidence || []).map((p) => <Thumb key={p} src={p} />)}</div>
            : <PhotoUpload value={capa.evidence || []} onChange={(evidence) => setCapa({ ...capa, evidence })} accept="image/*,application/pdf" label="Evidence" testid="capa-evidence" />}
          {!ro && <Button onClick={save} variant="outline" className="tap w-full border-slate-600 bg-transparent" data-testid="capa-save-btn">Save CAPA</Button>}
        </div>
        {!closed && inc.capa_missing?.length > 0 && (
          <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200" data-testid="capa-missing">
            <p className="flex items-center gap-2 font-semibold"><Lock className="h-4 w-4" />Closure blocked until complete:</p>
            <ul className="ml-6 list-disc">{inc.capa_missing.map((m) => <li key={m}>{m}</li>)}</ul>
          </div>
        )}
        {!closed && canClose && inc.capa_missing?.length === 0 && (
          <div className="space-y-2">
            <p className="eyebrow">Close-out signature</p>
            <SignaturePad onChange={setSig} testid="incident-close-signature" />
            <Button onClick={close} disabled={!sig} className="tap w-full bg-emerald-600 hover:bg-emerald-700" data-testid="incident-close-btn">Close incident</Button>
          </div>
        )}
        {closed && <p className="text-sm text-emerald-300" data-testid="incident-closed-info">Closed by {inc.closed_by} on {fmtDate(inc.closed_at)}</p>}
      </DialogContent>
    </Dialog>
  );
};

export default function Incidents() {
  const { canWrite } = useAuth();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/incidents").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const sel = rows.find((r) => r.id === params.get("id"));
  return (
    <div>
      <PageHeader eyebrow="Module 09" title="Accidents & Near Misses" description="Every incident requires root cause, corrective action, preventive action and verification evidence before it can be closed.">
        {canWrite && <Button onClick={() => setParams({ new: "1" })} className="tap bg-rose-600 hover:bg-rose-700" data-testid="new-incident-btn"><Plus className="mr-1 h-4 w-4" />Report incident</Button>}
      </PageHeader>
      <div className="panel divide-y divide-slate-800">
        {rows.map((r) => (
          <button key={r.id} onClick={() => setParams({ id: r.id })} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-800/40" data-testid={`incident-row-${r.ref}`}>
            <span>
              <span className="font-mono text-xs text-cyan-300">{r.ref}</span> <span className="font-semibold">{r.title}</span>
              <span className="block text-xs text-slate-400">{r.type.replace(/_/g, " ")} - {fmtDate(r.date)} - {r.location}</span>
            </span>
            <span className="flex flex-col items-end gap-1">
              <RagBadge status={r.status === "closed" ? "green" : r.capa_missing.length ? "red" : "amber"} label={r.status} />
              {r.status !== "closed" && <span className="text-[10px] text-slate-500">CAPA {6 - r.capa_missing.length}/6</span>}
            </span>
          </button>
        ))}
        {!rows.length && <p className="px-4 py-10 text-center text-sm text-slate-500">No incidents recorded.</p>}
      </div>
      <NewIncident open={params.get("new") === "1"} onOpenChange={(o) => !o && setParams({})} onDone={load} />
      <IncidentDetail inc={sel} onClose={() => setParams({})} onChange={(u) => setRows(rows.map((x) => (x.id === u.id ? u : x)))} />
    </div>
  );
}
